import { Decimal } from "decimal.js";
import { ZodString, core } from "zod";

import type { Writable } from "./shared.ts";

const DECIMAL_REGEXP = /^-?(0|[1-9]\d*)(\.\d+)?$/u;

const NON_NEGATIVE_DECIMAL_REGEXP = /^(0|[1-9]\d*)(\.\d+)?$/u;

const POSITIVE_DECIMAL_REGEXP = /^(0\.\d*[1-9]\d*|[1-9]\d*(\.\d+)?)$/u;

function isGreaterThan(a: string, b: number): boolean {
  return DECIMAL_REGEXP.test(a) && new Decimal(a).greaterThan(String(b));
}

function isGreaterThanOrEqual(a: string, b: number): boolean {
  return (
    DECIMAL_REGEXP.test(a) && new Decimal(a).greaterThanOrEqualTo(String(b))
  );
}

function isLessThan(a: string, b: number): boolean {
  return DECIMAL_REGEXP.test(a) && new Decimal(a).lessThan(String(b));
}

function isLessThanOrEqual(a: string, b: number): boolean {
  return DECIMAL_REGEXP.test(a) && new Decimal(a).lessThanOrEqualTo(String(b));
}

export interface ZodDecimal extends ZodString {
  readonly gt: (value: number) => ZodDecimal;
  readonly gte: (value: number) => ZodDecimal;
  readonly lt: (value: number) => ZodDecimal;
  readonly lte: (value: number) => ZodDecimal;
  /**
   * @deprecated Bounds the string length. Write `.lte(value)` instead.
   */
  readonly max: (
    maxLength: number,
    params?: core.$ZodCheckMaxLengthParams | string,
  ) => this;
  /**
   * @deprecated Bounds the string length. Write `.gte(value)` instead.
   */
  readonly min: (
    minLength: number,
    params?: core.$ZodCheckMinLengthParams | string,
  ) => this;
  readonly nonnegative: () => ZodDecimal;
  readonly positive: () => ZodDecimal;
}

const ZodDecimalSchema: core.$constructor<ZodDecimal> = core.$constructor(
  "ZodDecimal",
  (inst, def) => {
    ZodString.init(inst, def);
  },
);

const ZodDecimalProto: Writable<ZodDecimal> = Object.getPrototypeOf(
  new ZodDecimalSchema({ checks: [], type: "string" }),
);

ZodDecimalProto.gt = function gt(this: ZodDecimal, value: number): ZodDecimal {
  return this.superRefine((input, ctx) => {
    if (isLessThanOrEqual(input, value)) {
      ctx.addIssue({
        code: "too_small",
        inclusive: false,
        message: `Must be greater than ${value}`,
        minimum: value,
        origin: "number",
      });
    }
  });
};

ZodDecimalProto.gte = function gte(
  this: ZodDecimal,
  value: number,
): ZodDecimal {
  return this.superRefine((input, ctx) => {
    if (isLessThan(input, value)) {
      ctx.addIssue({
        code: "too_small",
        inclusive: true,
        message: `Must be greater than or equal to ${value}`,
        minimum: value,
        origin: "number",
      });
    }
  });
};

ZodDecimalProto.lt = function lt(this: ZodDecimal, value: number): ZodDecimal {
  return this.superRefine((input, ctx) => {
    if (isGreaterThanOrEqual(input, value)) {
      ctx.addIssue({
        code: "too_big",
        inclusive: false,
        maximum: value,
        message: `Must be less than ${value}`,
        origin: "number",
      });
    }
  });
};

ZodDecimalProto.lte = function lte(
  this: ZodDecimal,
  value: number,
): ZodDecimal {
  return this.superRefine((input, ctx) => {
    if (isGreaterThan(input, value)) {
      ctx.addIssue({
        code: "too_big",
        inclusive: true,
        maximum: value,
        message: `Must be less than or equal to ${value}`,
        origin: "number",
      });
    }
  });
};

ZodDecimalProto.nonnegative = function nonnegative(
  this: ZodDecimal,
): ZodDecimal {
  return this.regex(
    NON_NEGATIVE_DECIMAL_REGEXP,
    "Must be a non-negative decimal string",
  );
};

ZodDecimalProto.positive = function positive(this: ZodDecimal): ZodDecimal {
  return this.regex(
    POSITIVE_DECIMAL_REGEXP,
    "Must be a positive decimal string",
  );
};

export function decimal(): ZodDecimal {
  return new ZodDecimalSchema({ checks: [], type: "string" }).regex(
    DECIMAL_REGEXP,
    "Must be a decimal string",
  );
}
